/**
 * Actions the node has not resolved, held where leaving the screen cannot reach.
 *
 * A mark that was dispatched and never answered for is the one piece of state
 * in this application that its owner cannot reconstruct: they are the only
 * person who knows the mark should be on the sheet, and the key it was sent
 * under is the only thing that tells the node "this is that mark" rather than
 * "here is another one". Both used to live in `useState` on the drawing
 * screen, which is a fine owner for a selection and the wrong owner for this.
 *
 * The screen is a route. Tapping the crumb back to the register, or opening
 * the RFI a pin links to, unmounts it — the shell's outlet is keyed by project
 * and the router replaces the element, so there is no "the screen is still
 * there underneath" to rely on. Every unsent mark, every held change, every
 * gesture queued behind one and everything the reader had set aside went with
 * it, and coming back mounted a hook that had never heard of them. Nothing on
 * screen said so, because from the new hook's point of view nothing had
 * happened. The author is then in the worst of the two positions this module
 * spends its length avoiding: a write that may be on the drawing, and no way
 * to ask about it again under the name it went out with.
 *
 * So the lists live here, in the tab, and the hooks read and write them. What
 * that buys and what it does not:
 *
 * - **It survives navigation inside the application**, which is the boundary
 *   the drawing screen actually meets. Leave and come back and the held
 *   actions are the same rows, with the same keys and the same outcomes.
 * - **It does not survive the tab**, and nothing here writes to storage. A
 *   reload is still a loss, and saying otherwise in a docstring is how the
 *   sentence this one replaces came to be wrong. Cross-tab persistence is a
 *   different decision — what a second tab should do with a key the first one
 *   dispatched is not obvious — and is not made here.
 * - **It belongs to one signed-in session**, because the hold is one person's
 *   unfinished work and the next person at this browser is not them. Signing
 *   out ends that session, and so does the instance refusing the credential
 *   this browser was holding — which is a session ending whether or not
 *   anybody pressed anything. Signing in begins one.
 *
 * Ending a session empties the lists **and** invalidates them, which are two
 * different things and only the first is obvious. A request that was already
 * on its way when the session ended still answers, and the code waiting on it
 * then writes what it learned — a refusal onto the row it sent, a successor
 * released now that the one in front of it is done. Emptying the rows does
 * not stop any of that: the write simply puts the old session's work back,
 * into a store the next person is looking at. So each session has an era, a
 * write carries the era it was begun under, and a write from an era that has
 * ended is dropped rather than applied.
 *
 * Kept outside React rather than in a provider above the route for two
 * reasons. A provider is a component, so a screen putting a gesture down
 * during its own render would be updating a different component while
 * rendering — which React warns about and which is unsafe for exactly the
 * reason the warning gives. And a store with no component in it cannot be
 * remounted by accident, which is the whole failure being repaired.
 *
 * Every row carries what it was made on and is filtered by it at the point of
 * use: a store that outlives the screen is a store that can be read by the
 * wrong one.
 */

import { useCallback, useSyncExternalStore } from 'react'

type Listener = () => void

/** One named list. The name is the slot; the rows are whatever the hook holds. */
export type Held<T> = { readonly slot: string; readonly kind?: T }

const rows = new Map<string, readonly unknown[]>()
const watchers = new Map<string, Set<Listener>>()

/**
 * Which signed-in session the lists belong to.
 *
 * A counter rather than anything about the person: what has to be told apart
 * is *this* session from the one before it, and two sign-ins by the same
 * person are still two sessions. It is never reset, so an era that has ended
 * can never come round again.
 *
 * It is also the only thing in this tab that knows a session has ended, which
 * is why {@link useSessionEra} hands it to a screen that has to scope
 * something other than a held list by the same boundary.
 */
let era = 0

/** Watchers of the era itself, rather than of any one slot. */
const eraWatchers = new Set<Listener>()

// One shared empty array, so a slot nothing has been put in yet answers with
// the same reference every time. `useSyncExternalStore` compares snapshots by
// identity and a fresh `[]` per read is an infinite render.
const NOTHING: readonly unknown[] = []

/** Name a list. Called once, at module scope, beside the hook that owns it. */
export function heldActions<T>(slot: string): Held<T> {
  return { slot }
}

function read<T>(slot: string): readonly T[] {
  return (rows.get(slot) ?? NOTHING) as readonly T[]
}

/**
 * Read and write one held list, the way `useState` would.
 *
 * The setter takes the list as it stands *now* rather than as the render that
 * made the callback saw it, which is what these hooks need: every one of these
 * updates happens after an `await`, and several happen in the same tick.
 */
export function useHeld<T>(held: Held<T>): readonly T[] {
  const { slot } = held
  const subscribe = useCallback(
    (listener: Listener) => {
      const set = watchers.get(slot) ?? new Set<Listener>()
      watchers.set(slot, set)
      set.add(listener)
      return () => {
        set.delete(listener)
      }
    },
    [slot],
  )
  const snapshot = useCallback(() => read<T>(slot), [slot])
  return useSyncExternalStore(subscribe, snapshot, snapshot)
}

/**
 * Write one held list, the way `useState`'s setter would.
 *
 * The update is given the list as it stands *now* rather than as the render
 * that called this saw it, which is what these hooks need: every one of these
 * happens after an await, and several happen in the same tick.
 *
 * A plain function rather than something a hook hands back, so that a
 * `useCallback` that writes a held list has nothing to declare: a setter
 * returned from a hook is a value in the component's scope, and the
 * exhaustive-deps rule is right to ask about every one of those. This one is
 * not in the component's scope at all.
 */
export function setHeld<T>(
  held: Held<T>,
  update: (was: readonly T[]) => readonly T[],
  since: number = era,
): void {
  // From a session that has ended. The request this is the answer to was sent
  // by somebody who has since signed out, or whose credential the instance
  // refused, and whatever it established belongs to them — not to whoever is
  // at this browser now. Dropped here rather than only at the call sites,
  // because there are eleven of them across two hooks and the one that gets
  // forgotten is the one that matters.
  if (since !== era) return
  const { slot } = held
  const was = read<T>(slot)
  const next = update(was)
  // Identity, not contents: an update that decides nothing has changed is the
  // ordinary case here — a filter that removes nothing, a map that rewrites
  // nobody — and telling every screen about it is a render for no reason.
  if (next === was) return
  rows.set(slot, next)
  for (const listener of watchers.get(slot) ?? []) listener()
}

/**
 * One held list, read outside a render.
 *
 * For the dispatchers, which run across awaits and have to see the list as it
 * stands *now* rather than as the render that made them saw it. They used to
 * keep a ref for this, written on every render — which was right while the
 * list was the component's own state and became a bug the moment it was not:
 * a dispatcher whose component has since been unmounted holds a ref frozen at
 * the last render, so it kept finding a gesture it had already sent and sent
 * it again, for ever. The store has no such moment.
 */
export function heldNow<T>(held: Held<T>): readonly T[] {
  return read<T>(held.slot)
}

/**
 * The era a dispatch is running in, to be carried through its awaits.
 *
 * Read once, before the first request goes out, and handed to every write
 * that dispatch makes afterwards — and asked about again before anything is
 * dispatched *because* of what came back, which is the case a dropped write
 * cannot cover on its own: a successor released after its predecessor
 * settles is a new request, not a write.
 */
export function heldEra(): number {
  return era
}

/** Whether the session a dispatch began in is still the one at this browser. */
export function inHeldEra(since: number): boolean {
  return since === era
}

/**
 * End the session these lists belong to: let go of everything, in every slot.
 *
 * Signing out, a credential the instance has refused, signing in, and the
 * tests. Not a general "clear": there is no case in the application for
 * dropping one person's unresolved actions while they are still the person at
 * the screen.
 *
 * The era moves first, so that anything still on its way back is already out
 * of date by the time it answers — including a write racing this call.
 */
export function forgetHeldActions(): void {
  era += 1
  rows.clear()
  for (const [, set] of watchers) for (const listener of set) listener()
  for (const listener of eraWatchers) listener()
}

function subscribeToEra(listener: Listener): () => void {
  eraWatchers.add(listener)
  return () => {
    eraWatchers.delete(listener)
  }
}

/**
 * The era, as a value a render may depend on.
 *
 * {@link heldEra} is for a dispatcher running across awaits, which wants the
 * number as it stands now and no render at all. A screen needs the opposite:
 * it has to be told when the era moves, because what it built out of the old
 * one is no longer the answer.
 *
 * The one thing a session boundary is for, beyond the held lists. A cache in
 * this tab outlives the person who filled it -- the query store is made once
 * at module scope and the sign-in door does not empty it -- so anything kept
 * under a key that says only *what* it is can be handed to whoever signs in
 * next. A key that also says *which session asked* cannot: the next session
 * reads a key nothing has been written under, and an answer still on its way
 * to the last one settles under theirs, where nobody is looking. That is the
 * same rule {@link setHeld} applies to a write, arrived at from the other
 * side.
 *
 * Deliberately this counter rather than the person's id. Two sign-ins by the
 * same person are two sessions, their permissions may differ between them,
 * and an id would carry the first session's answers into the second.
 */
export function useSessionEra(): number {
  return useSyncExternalStore(subscribeToEra, heldEra, heldEra)
}
